import { LockFilled } from '@ant-design/icons';
import {
  Button,
  Form,
  Input,
  Radio,
  Skeleton,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { Logo } from 'assets';
import { AnimatePresence, motion } from 'framer-motion';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import IntlTelInput from 'react-intl-tel-input';
import { useRecoilState, useRecoilValue } from 'recoil';
import { twMerge } from 'tailwind-merge';

import 'react-phone-number-input/style.css';
import 'react-intl-tel-input/dist/main.css';

import { useAuth } from '@/hooks/useAuth';

import AddressModal from '@/components/CheckoutV2/Components/AddressModal';
import CheckoutV2Card from '@/components/CheckoutV2/Components/Card';
import PaymentAvailability from '@/components/CheckoutV2/Components/PaymentAvailability';
import ResponsiveSelect from '@/components/CheckoutV2/Components/ResponsiveSelect';
import StickyPayButton from '@/components/CheckoutV2/Components/StickyPayButton';
import RenderIf from '@/components/Common/RenderIf';
import { montserrat } from '@/components/Layout';
import { pageTitle } from '@/components/shared/basic/customHead';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import { getUserId } from '@/components/shared/getToken';
import { notify } from '@/components/shared/notify';

import { authModalControl } from '@/atoms/authModalControl';
import { user } from '@/atoms/user';
import appwriteService from '@/config/appwrite';
import {
  createAddressService,
  getAddresses,
  getAllCountries,
  getCities,
  getStates,
  updateAddress,
  updateDefaultAddress,
} from '@/services/Address';
import { checkEmailExist, createRazorpayPayment } from '@/services/Cart';
import {
  checkHardcopyUpgradeOffer,
  createHardcopyUpgradeOrder,
  HardcopyUpgradeOfferData,
} from '@/services/HardcopyUpgrade';
import { getSyncBookListApi } from '@/services/Library/Books';
import { ICities } from '@/utils/interfaces/Cities';
import { Address, AddressPayload } from '@/utils/interfaces/Manage-Addresses';
import { IStates } from '@/utils/interfaces/States';
import { User } from '@/utils/interfaces/User';

const UpgradeToHardcopyCheckoutPage: React.FC = () => {
  const router = useRouter();
  const [form] = Form.useForm();

  const productIdQuery = router.query.productId as string | undefined;

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [offerLoading, setOfferLoading] = useState<boolean>(true);
  const [offerData, setOfferData] = useState<HardcopyUpgradeOfferData | null>(null);
  const [offerError, setOfferError] = useState<string | null>(null);

  const [states, setStates] = useState<IStates[]>([]);
  const [cities, setCities] = useState<ICities[]>([]);
  const [getStatesLoading, setGetStatesLoading] = useState<boolean>(false);
  const [getCitiesLoading, setGetCitiesLoading] = useState<boolean>(false);

  const [addresses, setAddresses] = useState<Address[]>([]);
  const [address, setAddress] = useState<Address | null>(null);
  const [loadingAddress, setLoadingAddress] = useState<boolean>(false);
  const [addressModal, setAddressModal] = useState<{
    show: boolean;
    currentClickType: 'edit' | 'create' | null;
    addressForUpdate: Address | null;
  }>({ show: false, currentClickType: null, addressForUpdate: null });

  const [passwordStrength, setPasswordStrength] = useState<0 | 1 | 2 | 3 | 4>(0);
  const [emailAlreadyExists, setEmailAlreadyExists] = useState<boolean>(false);

  const [, setAuthModal] = useRecoilState(authModalControl);
  const { onSignUp } = useAuth();
  const userData: User | undefined = useRecoilValue(user);

  const paymentOption = Form.useWatch('paymentOption', form);
  const watchedCountryId = Form.useWatch('countryId', form);
  const watchedStateId = Form.useWatch('stateId', form);
  const watchedEmail = Form.useWatch('email', form);

  const phoneValidRef = useRef<boolean>(true);

  // Check auth session
  const checkIsAuthenticated = useCallback(async () => {
    try {
      const session = await appwriteService.account.getSession('current');
      if (session) {
        setIsAuthenticated(true);
      }
    } catch {
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    checkIsAuthenticated();
  }, [checkIsAuthenticated]);

  useEffect(() => {
    if (userData) {
      setIsAuthenticated(true);
      if (userData?.name) {
        form.setFieldsValue({ fullname: userData.name });
      }
      if (userData?.phone) {
        form.setFieldsValue({ phone: userData.phone });
      }
      if (userData?.email) {
        form.setFieldsValue({ email: userData.email });
      }
    }
  }, [userData, form]);

  // Preload Razorpay checkout script
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const existing = document.querySelector(
      'script[src="https://checkout.razorpay.com/v1/checkout.js"]',
    );
    if (!existing) {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  // Fetch upgrade offer data
  const fetchUpgradeOffer = useCallback(async (prodId: string | number) => {
    try {
      setOfferLoading(true);
      setOfferError(null);
      const res = await checkHardcopyUpgradeOffer(prodId);
      if (res?.success && res?.data) {
        setOfferData(res.data);
        if (!res.data.eligible) {
          setOfferError(
            res.data.message || 'You are not eligible for this upgrade offer.',
          );
        }
      } else {
        setOfferError(res?.message || 'Unable to retrieve upgrade offer details.');
      }
    } catch (err: any) {
      if (err?.response?.status === 401) {
        setOfferError('Please login to check your upgrade offer eligibility.');
      } else if (err?.response?.status === 422) {
        setOfferError(
          err?.response?.data?.message ||
            'Upgrade offer is not applicable for this product.',
        );
      } else {
        setOfferError(formateErrorObject(err));
      }
    } finally {
      setOfferLoading(false);
    }
  }, []);

  useEffect(() => {
    if (router.isReady && productIdQuery) {
      fetchUpgradeOffer(productIdQuery);
    }
  }, [router.isReady, productIdQuery, fetchUpgradeOffer]);

  // Load countries (India default)
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await getAllCountries();
        const countryList = res?.data || [];
        const india = countryList.find(
          (c: any) => c.code === 'IN' || c.name?.toLowerCase() === 'india',
        );
        if (india) {
          form.setFieldsValue({
            country: india.name,
            countryId: india.id,
          });
        }
      } catch (err) {
        console.error('Failed to load countries:', err);
      }
    };
    fetchCountries();
  }, [form]);

  // Fetch saved addresses if authenticated
  const fetchUserAddresses = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setLoadingAddress(true);
      const res = await getAddresses();
      const addrList = res || [];
      setAddresses(addrList);
      if (addrList.length) {
        const def = addrList.find((a: any) => a.set_default === 1) ?? addrList[0];
        setAddress(def);
        form.setFieldsValue({
          countryId: def?.country_id,
          stateId: def?.state_id,
          cityId: def?.city_id,
        });
      }
    } catch (err) {
      console.error('Failed to load addresses:', err);
    } finally {
      setLoadingAddress(false);
    }
  }, [isAuthenticated, form]);

  useEffect(() => {
    fetchUserAddresses();
  }, [fetchUserAddresses]);

  // Cascade: countryId -> states
  useEffect(() => {
    if (!watchedCountryId) return;
    let cancelled = false;
    setGetStatesLoading(true);
    getStates(watchedCountryId)
      .then((res) => {
        if (!cancelled && res?.data) {
          setStates(res.data);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => {
        if (!cancelled) setGetStatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [watchedCountryId]);

  // Cascade: stateId -> cities
  useEffect(() => {
    if (!watchedStateId) return;
    let cancelled = false;
    setGetCitiesLoading(true);
    getCities(watchedStateId)
      .then((res) => {
        if (!cancelled && res?.data) {
          setCities(res.data);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => {
        if (!cancelled) setGetCitiesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [watchedStateId]);

  const onStateChange = (_: any, option: any) => {
    setCities([]);
    form.setFieldValue('city', undefined);
    form.setFieldValue('cityId', undefined);
    const stateId = option?.lable as number;
    form.setFieldValue('stateId', stateId);
  };

  const onCityChange = (_: any, option: any) => {
    const cityId = option?.lable as number;
    form.setFieldValue('cityId', cityId);
  };

  const onEmailBlurHandle = async (e: any) => {
    const email = e.target.value?.trim();
    if (!email) return;
    try {
      const res = await checkEmailExist(email);
      setEmailAlreadyExists(Boolean(res?.data?.exist));
    } catch (err) {
      console.error(err);
    }
  };

  const getPasswordStrength = (value: string): 0 | 1 | 2 | 3 | 4 => {
    if (!value) return 0;
    if (value.length >= 8) return 4;
    return 1;
  };

  const passwordStrengthConfig = [
    null,
    { label: 'Weak', color: '#ef4444', bars: 1 },
    { label: 'Fair', color: '#f97316', bars: 2 },
    { label: 'Strong', color: '#22c55e', bars: 3 },
    { label: 'Very Strong', color: '#16a34a', bars: 4 },
  ] as const;

  const onFinishFailed = ({
    errorFields,
  }: {
    errorFields: { name: (string | number)[]; errors: string[] }[];
  }) => {
    if (!errorFields || errorFields.length === 0) return;
    const firstErrorFieldName = String(errorFields[0]?.name?.[0] ?? '');
    if (!firstErrorFieldName) return;

    const fieldAnchorMap: Record<string, string> = {
      email: 'form-anchor-email',
      fullname: 'form-anchor-fullname',
      phone: 'form-anchor-phone',
      password: 'form-anchor-password',
      address: 'form-anchor-address',
      country: 'form-anchor-country',
      pincode: 'form-anchor-pincode',
      state: 'form-anchor-state',
      city: 'form-anchor-city',
    };

    const anchorId = fieldAnchorMap[firstErrorFieldName];
    const el: HTMLElement | null =
      (anchorId ? document.getElementById(anchorId) : null) ||
      document.getElementById(`checkout-v2_${firstErrorFieldName}`) ||
      document.querySelector(`[name="${firstErrorFieldName}"]`);

    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top, behavior: 'smooth' });
      setTimeout(() => {
        try {
          const focusable = el.querySelector<HTMLElement>(
            'input, textarea, select',
          );
          (focusable ?? el).focus();
        } catch (e: any) {
          console.error(e);
        }
      }, 400);
    }
  };

  const showShippingAddressForm =
    !address ||
    addressModal.currentClickType === 'create' ||
    addressModal.currentClickType === 'edit';

  const onFinish = async (values: any) => {
    setLoading(true);

    try {
      // 1. Authenticate if guest
      if (!isAuthenticated) {
        const isEmailExists = await checkEmailExist(values.email);
        if (isEmailExists?.data?.exist) {
          setEmailAlreadyExists(true);
          setAuthModal((prev) => ({
            ...prev,
            login: true,
            email: values.email,
            emailExistsMessage: true,
          }));
          setLoading(false);
          return;
        }

        await onSignUp({
          email: values.email,
          password: values.password,
          name: values.fullname,
          phone: values.phone,
          phoneCountryCode: values.phoneCountryCode || 'in',
          phonePrefix: values.phonePrefix || '91',
          isModal: true,
        });

        await checkIsAuthenticated();
        notify('Account created successfully', 'success');
      }

      const userId = await getUserId();
      if (!userId) {
        throw new Error('User identifier not found. Please log in.');
      }

      // 2. Resolve Shipping Address ID
      let shippingAddressId: number | undefined = address?.id;

      if (
        addressModal.currentClickType === 'create' ||
        !isAuthenticated ||
        addresses.length === 0
      ) {
        const createRes = await createAddressService({
          address: values.address,
          city_id: values.cityId,
          country_id: values.countryId || 101,
          name: values.fullname,
          phone: values.phone,
          postal_code: values.pincode,
          set_default: 1,
          state_id: values.stateId,
          user_id: parseInt(userId),
          country_code: values.phoneCountryCode || 'in',
          iso2: values.phoneCountryCode || 'in',
        });
        shippingAddressId = createRes?.data?.id || createRes?.id;
      } else if (addressModal.currentClickType === 'edit' && isAuthenticated) {
        const updateData: AddressPayload = {
          id: addressModal.addressForUpdate?.id,
          address: values.address,
          city_id: values.cityId,
          country_id: values.countryId || 101,
          phone: values.phone,
          postal_code: values.pincode,
          set_default: 1,
          state_id: values.stateId,
          user_id: parseInt(userId),
          country_code: values.phoneCountryCode || 'in',
          iso2: values.phoneCountryCode || 'in',
        };
        const editRes = await updateAddress(updateData);
        shippingAddressId =
          editRes?.data?.id || addressModal.addressForUpdate?.id;
      }

      if (!shippingAddressId) {
        throw new Error('Please select or provide a valid shipping address.');
      }

      const targetProductId = Number(
        productIdQuery ||
          offerData?.hardcopy_product?.id,
      );

      if (!targetProductId) {
        throw new Error('Product not specified.');
      }

      const chosenPaymentType: 'razorpay' | 'cash_on_delivery' =
        values.paymentOption === 'cod' ? 'cash_on_delivery' : 'razorpay';

      // 3. Create Upgrade Order
      const payload: any = {
        product_id: targetProductId,
        address_id: Number(shippingAddressId),
        payment_type: chosenPaymentType,
        order_from: 'website',
      };

      if (chosenPaymentType === 'razorpay') {
        payload.payment_account = 'razorpay';
      }

      if (offerData?.source_order_id) {
        payload.source_order_id = Number(offerData.source_order_id);
      }

      const orderRes = await createHardcopyUpgradeOrder(payload);

      if (!orderRes?.success) {
        throw new Error(orderRes?.message || 'Failed to create upgrade order.');
      }

      // Flow A: Cash on Delivery
      if (chosenPaymentType === 'cash_on_delivery') {
        notify(
          orderRes?.data?.message ||
            'Hardcopy upgrade order placed successfully via Cash on Delivery.',
          'success',
        );
        setLoading(false);
        router.replace({
          pathname: '/order-successful',
          query: {
            orderId: orderRes?.data?.order_id,
            product_ids: targetProductId,
            callPurchased: 'purchased',
          },
        });
        return;
      }

      // Flow B: Online Payment via Razorpay
      if (chosenPaymentType === 'razorpay') {
        const orderData = orderRes?.data;
        const razorpayKey =
          orderData?.payment_account === 'razorpay_2'
            ? process.env.NEXT_PUBLIC_RAZORPAY_KEY_2!
            : process.env.NEXT_PUBLIC_RAZORPAY_KEY!;

        if (!(window as any).Razorpay) {
          throw new Error('Razorpay SDK failed to load. Please refresh and try again.');
        }

        const options = {
          key: razorpayKey,
          amount: Number(orderData?.amount || offerData?.upgrade_price || 1000) * 100,
          currency: orderData?.currency || 'INR',
          name: 'ZebraLearn',
          image: Logo,
          order_id: orderData?.razorpay_order_id,
          prefill: {
            name: orderData?.user_name || userData?.name || values?.fullname,
            email: orderData?.user_email || userData?.email || values?.email,
            contact: orderData?.user_phone || userData?.phone || values?.phone,
          },
          handler: async (paymentResponse: any) => {
            try {
              setLoading(true);
              await createRazorpayPayment({
                order_id: String(orderData?.order_id),
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature,
              });

              getSyncBookListApi();
              router.replace({
                pathname: '/order-successful',
                query: {
                  orderId: orderData?.order_id,
                  product_ids: targetProductId,
                  callPurchased: 'purchased',
                },
              });
            } catch (err: any) {
              notify(formateErrorObject(err), 'error');
            } finally {
              setLoading(false);
            }
          },
          modal: {
            escape: false,
            ondismiss: () => {
              setLoading(false);
            },
          },
        };

        const rzp = new (window as any).Razorpay(options);
        rzp.on('payment.failed', function (res: any) {
          console.error('Razorpay Payment Failed: ', res.error);
          notify(
            res.error?.description || 'Payment Failed. Please try again.',
            'error',
          );
          setLoading(false);
        });
        rzp.open();
      }
    } catch (err: any) {
      console.error(err);
      if (err?.response?.data?.message) {
        notify(err.response.data.message, 'error');
      } else {
        notify(formateErrorObject(err), 'error');
      }
      setLoading(false);
    }
  };

  const inputClassName = 'placeholder:text-grayZl-700';
  const inputParentClassName = 'flex lg:flex-row flex-col gap-4';
  const formItemClassName = 'flex-1 !m-0';
  const inputSize = 'large';
  const inputVarient = 'outlined';
  const formItemPrefixCls = 'checkout-v2';
  const cardChildClassName = ' space-y-4';
  const childCardClassName = 'bg-grayZl-925 rounded-xl p-4 gap-4';
  const priceClassName = 'text-sm font-semibold';

  const selectStyles = {
    popup: {
      root: { fontFamily: montserrat.style.fontFamily },
    },
  };

  const upgradePrice = offerData?.upgrade_price ?? 1000;
  const hardcopyProduct = offerData?.hardcopy_product;
  const originalUnitPrice = hardcopyProduct?.unit_price ?? 1690;
  const discountAmount = Math.max(0, originalUnitPrice - upgradePrice);

  return (
    <>
      <Head>
        <title>{pageTitle('Upgrade to Hardcopy Checkout')}</title>
      </Head>

      <div className='p-4 pb-24 md:pb-4 min-h-screen'>
        <div className='max-w-7xl mx-auto'>
          {/* Offer Loading State */}
          <RenderIf condition={offerLoading}>
            <div className='flex lg:flex-row flex-col flex-1 gap-4'>
              {/* Left Column Skeleton */}
              <div className='flex flex-col gap-4 flex-1'>
                <CheckoutV2Card title='Your information'>
                  <Skeleton active paragraph={{ rows: 3 }} />
                </CheckoutV2Card>
                <CheckoutV2Card title='Shipping address'>
                  <Skeleton active paragraph={{ rows: 4 }} />
                </CheckoutV2Card>
                <CheckoutV2Card title='Payment Method'>
                  <Skeleton active paragraph={{ rows: 2 }} />
                </CheckoutV2Card>
              </div>

              {/* Right Column Skeleton */}
              <div className='lg:min-w-[400px] lg:w-[400px] w-full flex flex-col gap-4'>
                <CheckoutV2Card>
                  <div className='flex flex-col gap-4'>
                    <div className='flex items-start gap-4'>
                      <div className='min-w-[90px] w-[90px] min-h-[90px] h-[90px] bg-grayZl-900 rounded-lg animate-pulse' />
                      <div className='flex-1 space-y-2 pt-1'>
                        <div className='h-3 bg-grayZl-850 rounded w-1/4 animate-pulse' />
                        <div className='h-4 bg-grayZl-850 rounded w-3/4 animate-pulse' />
                      </div>
                    </div>
                    <Skeleton active paragraph={{ rows: 4 }} />
                  </div>
                </CheckoutV2Card>
              </div>
            </div>
          </RenderIf>

          {/* Offer Ineligible / Error State */}
          <RenderIf condition={!offerLoading && Boolean(offerError && !offerData?.eligible)}>
            <div className='max-w-2xl mx-auto my-12 text-center'>
              <CheckoutV2Card title='Hardcopy Upgrade'>
                <div className='py-8 flex flex-col items-center gap-4'>
                  <div className='w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary text-2xl font-bold'>
                    !
                  </div>
                  <h3 className='text-xl font-semibold text-white'>
                    Upgrade Offer Unavailable
                  </h3>
                  <p className='text-grayZl-600 text-sm max-w-md'>
                    {offerError ||
                      'You must own the digital edition of this book to qualify for the physical hardcopy upgrade.'}
                  </p>
                  <div className='flex items-center gap-4 mt-4'>
                    <Button
                      size='large'
                      type='default'
                      onClick={() => router.back()}
                    >
                      Go Back
                    </Button>
                    <RenderIf condition={!isAuthenticated}>
                      <Button
                        size='large'
                        type='primary'
                        onClick={() =>
                          setAuthModal((prev) => ({
                            ...prev,
                            login: true,
                          }))
                        }
                      >
                        Login to ZebraLearn
                      </Button>
                    </RenderIf>
                  </div>
                </div>
              </CheckoutV2Card>
            </div>
          </RenderIf>

          {/* Main Checkout View (matching checkout-v2) */}
          <RenderIf condition={!offerLoading && (offerData?.eligible || !offerError)}>
            <Form
              form={form}
              name='upgrade-checkout-form'
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              className='max-w-full'
              scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
            >
              <AnimatePresence>
                <div className='flex lg:flex-row flex-col flex-1 gap-4'>
                  {/* Left Column: Form Details */}
                  <motion.div
                    key='checkout-left'
                    className='flex flex-col gap-4 flex-1'
                    initial={{ opacity: 0, x: -30, filter: 'blur(4px)' }}
                    animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                  >
                    {/* User Information Card */}
                    <CheckoutV2Card title='Your information'>
                      <>
                        <div className={twMerge('', inputParentClassName)}>
                          <RenderIf condition={!isAuthenticated}>
                            <div className='flex-1' id='form-anchor-email'>
                              <Form.Item
                                prefixCls={formItemPrefixCls}
                                className={twMerge('', formItemClassName)}
                                layout='vertical'
                                name='email'
                                rules={[
                                  {
                                    required: true,
                                    type: 'email',
                                    message: 'Please enter a valid email address',
                                  },
                                ]}
                              >
                                <Input
                                  className={inputClassName}
                                  size={inputSize}
                                  variant={inputVarient}
                                  onBlur={onEmailBlurHandle}
                                  placeholder='Email Address'
                                />
                              </Form.Item>
                              <RenderIf condition={emailAlreadyExists}>
                                <p
                                  className='text-red-500 text-xs mt-1 font-medium cursor-pointer'
                                  onClick={() =>
                                    setAuthModal((prevState) => ({
                                      ...prevState,
                                      login: true,
                                      email: watchedEmail?.trim(),
                                      emailExistsMessage: true,
                                    }))
                                  }
                                >
                                  Email already exists,{' '}
                                  <span className='underline font-semibold'>
                                    please login
                                  </span>
                                  .
                                </p>
                              </RenderIf>
                            </div>
                          </RenderIf>
                          <div className='flex-1' id='form-anchor-fullname'>
                            <Form.Item
                              prefixCls={formItemPrefixCls}
                              className={twMerge('flex-1', formItemClassName)}
                              layout='vertical'
                              name='fullname'
                              rules={[
                                {
                                  required: true,
                                  message: 'Please enter your full name',
                                },
                              ]}
                            >
                              <Input
                                className={inputClassName}
                                size={inputSize}
                                variant={inputVarient}
                                placeholder='Full Name'
                              />
                            </Form.Item>
                          </div>
                        </div>

                        <div className={twMerge('', inputParentClassName)}>
                          <div className='flex-1' id='form-anchor-phone'>
                            <Form.Item
                              prefixCls={formItemPrefixCls}
                              style={{ marginBottom: 0 }}
                              className={twMerge('flex-1', formItemClassName)}
                              layout='vertical'
                              name='phone'
                              rules={[
                                {
                                  validator: (_, value) => {
                                    if (!value || value.trim() === '') {
                                      return Promise.reject(
                                        new Error('Please input your phone number!'),
                                      );
                                    }
                                    if (!phoneValidRef.current) {
                                      return Promise.reject(
                                        new Error('Please enter a valid phone number'),
                                      );
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                            >
                              <IntlTelInput
                                placeholder='Phone Number'
                                autoPlaceholder={false}
                                defaultCountry='in'
                                containerClassName='flex intl-tel-input z-10 w-full ant-input ant-input-lg ant-input-outlined checkout-intl-tel-input'
                                inputClassName={
                                  'flex-1 bg-transparent outline-none ant-input ant-input-lg ant-input-status-success ps-14 ' +
                                  inputClassName
                                }
                                onSelectFlag={(_number, countryData) => {
                                  form.setFieldsValue({
                                    phoneCountryCode:
                                      countryData.iso2?.toLowerCase() || 'in',
                                    phonePrefix: countryData.dialCode || '91',
                                  });
                                  form.validateFields(['phone']);
                                }}
                                onPhoneNumberChange={(isValid, value, countryData) => {
                                  phoneValidRef.current = isValid;
                                  form.setFieldsValue({
                                    phone: value,
                                    phoneCountryCode:
                                      countryData.iso2?.toLowerCase() || 'in',
                                    phonePrefix: countryData.dialCode || '91',
                                  });
                                  form.validateFields(['phone']);
                                }}
                              />
                            </Form.Item>
                          </div>

                          <RenderIf condition={!isAuthenticated}>
                            <div className={twMerge('', formItemClassName)}>
                              <Form.Item
                                prefixCls={formItemPrefixCls}
                                className='!m-0'
                                layout='vertical'
                                name='password'
                                rules={[
                                  { required: true, message: 'Please create a password' },
                                  {
                                    min: 8,
                                    message: 'Password must be at least 8 characters',
                                  },
                                ]}
                              >
                                <Input.Password
                                  classNames={{
                                    input: 'placeholder:text-grayZl-700',
                                  }}
                                  className={inputClassName}
                                  size={inputSize}
                                  variant={inputVarient}
                                  placeholder='Create Account Password'
                                  onChange={(e) =>
                                    setPasswordStrength(
                                      getPasswordStrength(e.target.value),
                                    )
                                  }
                                />
                              </Form.Item>
                              <RenderIf condition={passwordStrength > 0}>
                                <div style={{ marginTop: 8 }}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      gap: 4,
                                      marginBottom: 4,
                                    }}
                                  >
                                    {[1, 2, 3, 4].map((bar) => (
                                      <div
                                        key={bar}
                                        style={{
                                          flex: 1,
                                          height: 4,
                                          borderRadius: 4,
                                          backgroundColor:
                                            bar <= passwordStrength
                                              ? passwordStrengthConfig[passwordStrength]
                                                  ?.color
                                              : '#374151',
                                          transition: 'background-color 0.3s ease',
                                        }}
                                      />
                                    ))}
                                  </div>
                                  <p
                                    style={{
                                      fontSize: 12,
                                      color:
                                        passwordStrengthConfig[passwordStrength]
                                          ?.color,
                                      margin: 0,
                                      fontWeight: 500,
                                    }}
                                  >
                                    {passwordStrengthConfig[passwordStrength]?.label}
                                  </p>
                                </div>
                              </RenderIf>
                            </div>
                          </RenderIf>
                        </div>
                      </>
                    </CheckoutV2Card>

                    {/* Shipping Address Card */}
                    <CheckoutV2Card title='Shipping Address'>
                      <>
                        <div className='flex justify-end gap-2'>
                          <RenderIf condition={Boolean(addresses?.length)}>
                            <button
                              onClick={() =>
                                setAddressModal((prev) => ({
                                  ...prev,
                                  show: true,
                                }))
                              }
                              type='button'
                              className='self-end text-primary font-medium text-sm px-2 py-1 rounded'
                            >
                              Change Address
                            </button>
                          </RenderIf>
                        </div>

                        <RenderIf condition={loadingAddress}>
                          <Skeleton active />
                        </RenderIf>

                        {/* Selected Address Display */}
                        <RenderIf
                          condition={
                            Boolean(address) &&
                            addressModal.currentClickType === null &&
                            !loadingAddress
                          }
                        >
                          <div className={twMerge('', childCardClassName)}>
                            <div className='flex gap-2 items-start'>
                              <Radio value={1} checked />
                              <div>
                                <p className='text-sm text-white'>
                                  {address?.address}, {address?.postal_code},{' '}
                                  {address?.city_name}, {address?.state_name},{' '}
                                  {address?.country_name}
                                </p>
                              </div>
                            </div>
                          </div>
                        </RenderIf>

                        {/* Address Entry / Edit Form */}
                        <RenderIf condition={showShippingAddressForm}>
                          <div id='form-anchor-address'>
                            <Form.Item
                              prefixCls={formItemPrefixCls}
                              layout='vertical'
                              name='address'
                              rules={[
                                {
                                  required: true,
                                  message: 'Please enter your complete address',
                                },
                              ]}
                            >
                              <TextArea
                                size={inputSize}
                                variant={inputVarient}
                                className={inputClassName}
                                rows={4}
                                placeholder='Type your complete address'
                              />
                            </Form.Item>
                          </div>

                          <div className={cardChildClassName}>
                            <div className={twMerge('', inputParentClassName)}>
                              <div className='flex-1' id='form-anchor-country'>
                                <Form.Item
                                  prefixCls={formItemPrefixCls}
                                  className='flex-1'
                                  layout='vertical'
                                  name='country'
                                  rules={[{ required: true }]}
                                  initialValue='India'
                                >
                                  <Input
                                    disabled
                                    className={inputClassName}
                                    size={inputSize}
                                    variant={inputVarient}
                                    value='India'
                                  />
                                </Form.Item>
                              </div>

                              <div className='flex-1' id='form-anchor-pincode'>
                                <Form.Item
                                  prefixCls={formItemPrefixCls}
                                  className='flex-1'
                                  layout='vertical'
                                  name='pincode'
                                  validateTrigger={['onChange', 'onBlur']}
                                  rules={[
                                    {
                                      required: true,
                                      message: 'Please enter pincode',
                                    },
                                    {
                                      pattern: /^[1-9][0-9]{5}$/,
                                      message: 'Please enter a valid 6-digit pincode',
                                    },
                                  ]}
                                >
                                  <Input
                                    className={inputClassName}
                                    size={inputSize}
                                    variant={inputVarient}
                                    placeholder='Enter Pincode'
                                    maxLength={6}
                                  />
                                </Form.Item>
                              </div>
                            </div>

                            <div className={twMerge('', inputParentClassName)}>
                              <div className='flex-1' id='form-anchor-state'>
                                <Form.Item
                                  prefixCls={formItemPrefixCls}
                                  className='flex-1'
                                  layout='vertical'
                                  name='state'
                                  rules={[
                                    {
                                      required: true,
                                      message: 'Please select a state',
                                    },
                                  ]}
                                >
                                  <ResponsiveSelect
                                    prefixCls={formItemPrefixCls}
                                    size={inputSize}
                                    variant={inputVarient}
                                    className={inputClassName}
                                    styles={selectStyles}
                                    onChange={onStateChange}
                                    loading={getStatesLoading}
                                    options={states.map((i) => ({
                                      lable: i.id,
                                      value: i.name,
                                    }))}
                                    placeholder='Select State'
                                  />
                                </Form.Item>
                              </div>

                              <div className='flex-1' id='form-anchor-city'>
                                <Form.Item
                                  prefixCls={formItemPrefixCls}
                                  className='flex-1'
                                  layout='vertical'
                                  name='city'
                                  rules={[
                                    {
                                      required: true,
                                      message: 'Please select a city',
                                    },
                                  ]}
                                >
                                  <ResponsiveSelect
                                    prefixCls={formItemPrefixCls}
                                    size={inputSize}
                                    variant={inputVarient}
                                    className={inputClassName}
                                    styles={selectStyles}
                                    onChange={onCityChange}
                                    loading={getCitiesLoading}
                                    options={cities.map((i) => ({
                                      lable: i.id,
                                      value: i.name,
                                    }))}
                                    placeholder='Select City'
                                  />
                                </Form.Item>
                              </div>
                            </div>
                          </div>
                        </RenderIf>
                      </>
                    </CheckoutV2Card>

                    {/* Payment Information Card */}
                    <CheckoutV2Card title='Payment information'>
                      <>
                        <Form.Item
                          prefixCls={formItemPrefixCls}
                          layout='vertical'
                          name='paymentOption'
                          initialValue='online'
                        >
                          <Radio.Group className='flex flex-col gap-4 w-full'>
                            <div
                              onClick={() =>
                                form.setFieldValue('paymentOption', 'online')
                              }
                              className={twMerge(
                                'flex-1 flex items-start gap-0 rounded-xl p-4 cursor-pointer transition-colors duration-200 hover:bg-grayZl-925',
                                paymentOption === 'online'
                                  ? 'bg-grayZl-925'
                                  : 'bg-transparent',
                              )}
                            >
                              <Radio
                                value='online'
                                style={{ alignItems: 'flex-start' }}
                              />
                              <PaymentAvailability />
                            </div>

                            <div
                              onClick={() =>
                                form.setFieldValue('paymentOption', 'cod')
                              }
                              className={twMerge(
                                'flex-1 rounded-xl p-4 cursor-pointer min-w-[180px] transition-colors duration-200 hover:bg-grayZl-925',
                                '[&_.ant-radio-inner]:border-grayZl-600',
                                '[&_.ant-radio-checked_.ant-radio-inner]:border-grayZl-600',
                                paymentOption === 'cod'
                                  ? 'bg-grayZl-925'
                                  : 'bg-transparent',
                              )}
                            >
                              <Radio value='cod' className='text-grayZl-600'>
                                Cash On Delivery (COD)
                              </Radio>
                            </div>
                          </Radio.Group>
                        </Form.Item>
                      </>
                    </CheckoutV2Card>
                  </motion.div>

                  {/* Right Column: Order Summary */}
                  <motion.div
                    key='checkout-right'
                    initial={{ opacity: 0, x: 30, filter: 'blur(4px)' }}
                    animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                    transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
                  >
                    <CheckoutV2Card className='lg:min-w-[400px] lg:w-[400px] w-full flex flex-col gap-4'>

                      {/* Product Detail Card */}
                      <div
                        className={twMerge(
                          'flex flex-col overflow-auto',
                          childCardClassName,
                        )}
                      >
                        <div className='flex items-start gap-4'>
                          <div className='min-w-[90px] w-[90px] min-h-[90px] max-h-[90px] h-[90px] p-1 flex items-center justify-center bg-black/40 rounded-lg'>
                            {hardcopyProduct?.thumbnail_img ? (
                              <Image
                                className='w-auto h-full object-contain'
                                src={hardcopyProduct.thumbnail_img}
                                alt={hardcopyProduct.name || 'Book Thumbnail'}
                                width={90}
                                height={90}
                              />
                            ) : (
                              <div className='w-full h-full bg-grayZl-900 rounded flex items-center justify-center text-xs text-grayZl-600'>
                                Book
                              </div>
                            )}
                          </div>
                          <div className='flex-1'>
                            <p className='text-sm text-grayZl-600'>Qty : 1</p>
                            <p className='text-sm font-medium text-white line-clamp-2'>
                              {hardcopyProduct?.name || 'Futures & Options Blueprint'}
                            </p>
                          </div>
                          <div className='text-right'>
                            <p className={twMerge('whitespace-nowrap text-white', priceClassName)}>
                              ₹{upgradePrice} /-
                            </p>
                            {originalUnitPrice > upgradePrice ? (
                              <p
                                className={twMerge(
                                  'whitespace-nowrap line-through text-grayZl-600 text-sm font-semibold',
                                  priceClassName,
                                )}
                              >
                                ₹{originalUnitPrice} /-
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      {/* Summary Breakdown */}
                      <div
                        className={twMerge(
                          'p-4 flex flex-col gap-3',
                          childCardClassName,
                        )}
                      >
                        <div className='flex items-start justify-between text-sm text-grayZl-300'>
                          <p>Hardcopy Price</p>
                          <p className={twMerge('', priceClassName)}>
                            ₹{originalUnitPrice}
                          </p>
                        </div>

                        {discountAmount > 0 && (
                          <div className='flex items-start justify-between text-sm text-green-400'>
                            <p>Upgrade Discount</p>
                            <p className={twMerge('', priceClassName)}>
                              - ₹{discountAmount}
                            </p>
                          </div>
                        )}

                        <div className='flex items-start justify-between text-sm text-grayZl-300'>
                          <p>Shipping Charges</p>
                          <p className={twMerge('text-green-500', priceClassName)}>
                            FREE
                          </p>
                        </div>

                        <div className='flex items-start justify-between text-sm text-grayZl-400'>
                          <p>GST & Taxes</p>
                          <p className='text-xs'>Included</p>
                        </div>

                        <div className='h-px bg-grayZl-800 w-full my-1'></div>

                        <div className='flex items-start justify-between text-base font-semibold text-white'>
                          <p>To Pay</p>
                          <p className={twMerge('text-lg text-primary', priceClassName)}>
                            ₹{upgradePrice}
                          </p>
                        </div>
                      </div>

                      {/* Desktop Submit Button */}
                      <Form.Item
                        prefixCls={formItemPrefixCls}
                        className='m-0 p-0 hidden md:block'
                      >
                        <Button
                          loading={loading}
                          disabled={loading}
                          className='w-full'
                          size='large'
                          icon={<LockFilled />}
                          type='primary'
                          htmlType='submit'
                        >
                          Proceed to Pay ₹{upgradePrice}
                        </Button>
                      </Form.Item>

                      {/* Hidden Fields */}
                      <Form.Item
                        prefixCls={formItemPrefixCls}
                        name='phoneCountryCode'
                        hidden
                        initialValue='in'
                      />
                      <Form.Item
                        prefixCls={formItemPrefixCls}
                        name='phonePrefix'
                        hidden
                        initialValue='91'
                      />
                      <Form.Item
                        prefixCls={formItemPrefixCls}
                        name='countryId'
                        hidden
                        initialValue={101}
                      />
                      <Form.Item
                        prefixCls={formItemPrefixCls}
                        name='stateId'
                        hidden
                        initialValue={null}
                      />
                      <Form.Item
                        prefixCls={formItemPrefixCls}
                        name='cityId'
                        hidden
                        initialValue={null}
                      />
                    </CheckoutV2Card>
                  </motion.div>
                </div>
              </AnimatePresence>

              {/* Mobile Sticky Pay Button */}
              <StickyPayButton
                loading={loading}
                disabled={loading}
                currencySymbol='₹'
                grandTotal={upgradePrice}
              />
            </Form>
          </RenderIf>

          {/* Address Modal for selecting / creating address */}
          <AddressModal
            open={addressModal.show}
            addresses={addresses}
            selectedAddress={address}
            onCancel={() =>
              setAddressModal((prev) => ({ ...prev, show: false }))
            }
            onSelect={(a) => {
              setAddress(a);
              setAddressModal((prev) => ({
                ...prev,
                show: false,
                currentClickType: null,
              }));
              form.setFieldsValue({
                countryId: a?.country_id,
                stateId: a?.state_id,
                cityId: a?.city_id,
              });
            }}
            onClickCreate={() => {
              setAddressModal((prev) => ({
                ...prev,
                show: false,
                currentClickType: 'create',
              }));
              form.setFieldsValue({
                address: null,
                state: null,
                stateId: null,
                city: null,
                cityId: null,
                pincode: null,
              });
            }}
            onClickEdit={async (a) => {
              setAddressModal((prev) => ({
                ...prev,
                show: false,
                currentClickType: 'edit',
                addressForUpdate: a,
              }));
              form.setFieldsValue({
                address: a.address,
                country: a.country_name || 'India',
                countryId: parseInt(a?.country_id || '101'),
                state: a.state_name,
                stateId: parseInt(a?.state_id || '0') || undefined,
                city: a.city_name,
                cityId: parseInt(a?.city_id || '0') || undefined,
                pincode: a.postal_code,
              });
              if (a?.state_id) {
                const cityRes = await getCities(parseInt(a.state_id));
                if (cityRes?.data) setCities(cityRes.data);
              }
            }}
            onSetDefault={async (a) => {
              await updateDefaultAddress(a.id);
              const updated = await getAddresses();
              setAddresses(updated);
            }}
          />
        </div>
      </div>
    </>
  );
};

export default UpgradeToHardcopyCheckoutPage;
